import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { listLessons, loadLesson, LessonView } from '../Lesson';
import { identifyPetParent, usePageView } from '../Hub/analytics';
import { hashids } from '../../utilities';
import PublicHeader from './PublicHeader';
import { hubHandoffTarget, isCrossOrigin } from './hubOrigin';

// Logged-out lesson page, served at the paths in Column F of the lessons sheet.
// The :category segment exists only so those URLs resolve — the slug is the key.
// Renders the same LessonView the hub uses, so content can never diverge.
export default function PublicLessonPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const lesson = React.useMemo(() => loadLesson(slug), [slug]);

  React.useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // `?pp=<hashid>` attributes this visit to the pet parent the link was shared
  // with. The hashid is opaque (same encoding as the /profiles#id= links) so no
  // personal data travels in the URL, and it decodes to the numeric id the Hub
  // identifies with, keeping public and in-hub events on one Mixpanel profile.
  //
  // Declared before usePageView so the identify runs first — effects fire in hook
  // order, and an unattributed page_view cannot be re-attributed later.
  const ppParam = new URLSearchParams(window.location.search).get('pp');
  React.useEffect(() => {
    if (!ppParam) return;
    const [petParentId] = hashids.decode(ppParam);
    if (petParentId != null) identifyPetParent(String(petParentId));
  }, [ppParam]);

  usePageView(
    { lesson_name: lesson?.title, from_where: 'public_lesson' },
    [lesson?.slug],
    !!lesson,
  );

  // A `?t=` token upgrades this public URL into a hub session (Zendesk "Copy",
  // macros with the ticket-field token). Hand off to the existing magic-link
  // route rather than duplicating auth; if the token is bad, AuthenticateToken
  // sends the reader back here, so the link can never dead-end.
  //
  // The handoff must land on the Hub's own origin: this page is also served from
  // learning.petcademy.org, and a session created there would be separate from the
  // one on app.petcademy.org (localStorage is per-origin). See ./hubOrigin.
  React.useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('t');
    if (!token || !lesson) return;
    // ?from=learning lets the Hub record this read as a learning.petcademy.org view.
    const path = `/authenticate?token=${encodeURIComponent(token)}`
      + `&redirect=${encodeURIComponent(`/hub/lessons-and-guides/${lesson.slug}?from=learning`)}`;
    const target = hubHandoffTarget(path, window.location.hostname);
    if (isCrossOrigin(target)) window.location.replace(target);
    else navigate(target, { replace: true });
  }, [lesson, navigate]);

  if (!lesson) {
    return (
      <>
        <PublicHeader />
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography>We couldn&apos;t find that lesson.</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <PublicHeader />
      <LessonView
        lesson={lesson}
        // Related lessons stay on the public site, at their own public paths.
        onNavigate={(next) => {
          const target = listLessons().find((l) => l.slug === next);
          if (target) navigate(target.publicPath);
        }}
      />
    </>
  );
}
